import React, { useState, useRef } from 'react';
import { Autocomplete } from '@react-google-maps/api';
import { Customer } from '../types/types';
import { CheckIcon, PlusIcon } from 'lucide-react';
interface AddCustomerFormProps {
  addCustomer: (customer: Customer) => void;
}
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const AddCustomerForm: React.FC<AddCustomerFormProps> = ({
  addCustomer
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [availableDays, setAvailableDays] = useState<string[]>(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const [timeOnSite, setTimeOnSite] = useState(60);
  const [location, setLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry && place.geometry.location) {
        setAddress(place.formatted_address || '');
        setLocation({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
      }
    }
  };
  const toggleDay = (day: string) => {
    if (availableDays.includes(day)) {
      setAvailableDays(availableDays.filter(d => d !== day));
    } else {
      setAvailableDays([...availableDays, day]);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !address || !location || availableDays.length === 0) {
      alert('Please fill in all required fields');
      return;
    }
    const newCustomer: Customer = {
      id: '',
      name,
      address,
      location,
      availableDays,
      timeOnSite,
      selected: true
    };
    addCustomer(newCustomer);
    // Reset form
    setName('');
    setAddress('');
    setTimeOnSite(60);
  };
  return <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 p-4 rounded-lg" data-id="element-17">
      <div data-id="element-18">
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-19">
          Customer Name
        </label>
        <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-20" />
      </div>
      <div data-id="element-21">
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-22">
          Address
        </label>
        <Autocomplete onLoad={autocomplete => {
        autocompleteRef.current = autocomplete;
      }} onPlaceChanged={handlePlaceSelect} restrictions={{
        country: 'it'
      }} data-id="element-23">
          <input type="text" id="address" value={address} onChange={e => setAddress(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Enter address" required data-id="element-24" />
        </Autocomplete>
      </div>
      <div data-id="element-25">
        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-26">
          Available Days
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-27">
          {daysOfWeek.map(day => <button key={day} type="button" onClick={() => toggleDay(day)} className={`px-3 py-1 text-sm rounded-md transition-colors ${availableDays.includes(day) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`} data-id="element-28">
              {day.substring(0, 3)}
              {availableDays.includes(day) && <CheckIcon className="inline-block ml-1 w-4 h-4" data-id="element-29" />}
            </button>)}
        </div>
      </div>
      <div data-id="element-30">
        <label htmlFor="timeOnSite" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-31">
          Time on Site (minutes)
        </label>
        <input type="number" id="timeOnSite" min="5" max="480" value={timeOnSite} onChange={e => setTimeOnSite(parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" required data-id="element-32" />
      </div>
      <button type="submit" className="w-full flex justify-center items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-33">
        <PlusIcon className="w-5 h-5 mr-2" data-id="element-34" />
        Add Customer
      </button>
    </form>;
};
export default AddCustomerForm;